import React, { useEffect, useState } from "react";
import { Select, SelectItem } from "@heroui/react";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";

import { useWebuildAPIClient } from "@/providers/WeBuildClientProvider";
import { useWorkflowStore } from "@/stores/WorkflowStore";
import { logger } from "@/telemetry";

interface ToolPropertiesPanelProps {
  toolId: string;
  toolNodeId: string;
}

export const ToolPropertiesPanel: React.FC<ToolPropertiesPanelProps> = ({ toolId, toolNodeId }) => {
  const { t } = useTranslation();
  const { platformClient } = useWebuildAPIClient();
  const { workflow, setWorkflow } = useWorkflowStore();
  const [availableTools, setAvailableTools] = useState<
    Array<{ id: string; name: string; description: string }>
  >([]);

  useEffect(() => {
    const fetchTools = async () => {
      if (!platformClient) return;
      try {
        const tools = await platformClient.tools.list();

        setAvailableTools(
          tools.map((tool: any) => ({
            id: tool.id,
            name: tool.name,
            description: tool.description || "",
          })),
        );
      } catch (error) {
        logger.error("Failed to fetch tools", error);
      }
    };

    fetchTools();
  }, [platformClient]);

  const selectedTool = availableTools.find((t) => t.id === toolId);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-4">
        <Icon className="w-5 h-5 text-gray-500" icon="qlementine-icons:tool-16" />
        <h3 className="text-sm font-semibold">{t("Tool Configuration")}</h3>
      </div>

      <Select
        label={t("Tool")}
        placeholder={t("Select a tool")}
        selectedKeys={toolId ? [toolId] : []}
        size="sm"
        onSelectionChange={(keys) => {
          const selected = Array.from(keys)[0] as string;

          if (selected && selected !== toolId && workflow) {
            // Extract agentId from toolNodeId format: {agentId}_tool_{toolId}
            const agentId = toolNodeId.split("_tool_")[0];
            const agent = workflow.agents[agentId];

            if (agent) {
              const currentTools = (agent.parameters?.tools as string[]) || [];

              setWorkflow({
                ...workflow,
                agents: {
                  ...workflow.agents,
                  [agentId]: {
                    ...agent,
                    parameters: {
                      ...agent.parameters,
                      tools: currentTools.map((t) => (t === toolId ? selected : t)),
                    },
                  },
                },
              });
            }
          }
        }}
      >
        {availableTools.map((tool) => (
          <SelectItem key={tool.id} textValue={tool.name}>
            <div className="flex flex-col">
              <span className="text-sm">{tool.name}</span>
              {tool.description && (
                <span className="text-xs text-default-400">{tool.description}</span>
              )}
            </div>
          </SelectItem>
        ))}
      </Select>

      {selectedTool && (
        <div className="p-3 rounded-lg bg-default-100">
          <p className="text-xs font-medium mb-1">{selectedTool.name}</p>
          <p className="text-xs text-default-500">{selectedTool.description}</p>
        </div>
      )}
    </div>
  );
};
